import React, { useMemo, useState } from "react";
import { T } from "../theme";
import { Badge, CONDITIONS, fmt, GENRES, TapeIcon } from "../ui";

const SORTS = {
  recent: (a, b) => 0, // already ordered by createdAt desc from Firestore
  artist: (a, b) => (a.artist || "").localeCompare(b.artist || ""),
  album: (a, b) => (a.albumTitle || "").localeCompare(b.albumTitle || ""),
  year: (a, b) => (b.releaseYear || 0) - (a.releaseYear || 0),
  value: (a, b) => (b.estimatedValue || 0) - (a.estimatedValue || 0),
};

export default function CollectionScreen({ tapes, onSelect, onAdd }) {
  const [q, setQ] = useState("");
  const [genre, setGenre] = useState("All");
  const [cond, setCond] = useState("All");
  const [sort, setSort] = useState("recent");
  const [view, setView] = useState("grid");

  const filtered = useMemo(() => {
    const list = tapes.filter((t) => {
      const needle = q.toLowerCase();
      const mq = !needle || t.artist?.toLowerCase().includes(needle) || t.albumTitle?.toLowerCase().includes(needle);
      const mg = genre === "All" || t.genre === genre;
      const mc = cond === "All" || t.condition === cond;
      return mq && mg && mc;
    });
    return [...list].sort(SORTS[sort]);
  }, [tapes, q, genre, cond, sort]);

  return (
    <div>
      <div style={{ position: "relative", marginBottom: 10 }}>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search artist or album…"
          style={{ width: "100%", background: T.inputBg, border: `1px solid ${T.border}`, borderRadius: 8, color: T.text, padding: "10px 36px 10px 12px", fontSize: 13, fontFamily: T.font, outline: "none", boxSizing: "border-box" }} />
        <span style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", color: T.muted, fontSize: 16 }}>🔍</span>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
        <select value={genre} onChange={(e) => setGenre(e.target.value)} style={{ background: T.inputBg, border: `1px solid ${T.border}`, borderRadius: 6, color: T.text, padding: "8px 10px", fontSize: 12, fontFamily: T.font, outline: "none" }}>
          <option value="All">All Genres</option>
          {GENRES.map((g) => <option key={g}>{g}</option>)}
        </select>
        <select value={cond} onChange={(e) => setCond(e.target.value)} style={{ background: T.inputBg, border: `1px solid ${T.border}`, borderRadius: 6, color: T.text, padding: "8px 10px", fontSize: 12, fontFamily: T.font, outline: "none" }}>
          <option value="All">All Conditions</option>
          {CONDITIONS.map((c) => <option key={c}>{c}</option>)}
        </select>
      </div>
      <div style={{ marginBottom: 12 }}>
        <select value={sort} onChange={(e) => setSort(e.target.value)} style={{ width: "100%", background: T.inputBg, border: `1px solid ${T.border}`, borderRadius: 6, color: T.text, padding: "8px 10px", fontSize: 12, fontFamily: T.font, outline: "none" }}>
          <option value="recent">Sort: Recently Added</option>
          <option value="artist">Sort: Artist</option>
          <option value="album">Sort: Album</option>
          <option value="year">Sort: Year</option>
          <option value="value">Sort: Estimated Value</option>
        </select>
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
        <span style={{ fontSize: 10, color: T.muted, letterSpacing: "0.15em", textTransform: "uppercase" }}>{filtered.length} tapes</span>
        <div style={{ display: "flex", gap: 6 }}>
          {["grid", "list"].map((v) => (
            <button key={v} onClick={() => setView(v)} style={{ background: view === v ? T.accent : "transparent", border: `1px solid ${view === v ? T.accent : T.border}`, color: view === v ? "#fff" : T.muted, padding: "4px 10px", borderRadius: 5, cursor: "pointer", fontSize: 11, fontFamily: T.font }}>{v === "grid" ? "▦" : "≡"}</button>
          ))}
        </div>
      </div>

      {filtered.length === 0 ? (
        <div style={{ textAlign: "center", padding: 32 }}>
          <div style={{ fontSize: 13, color: T.muted, marginBottom: 10 }}>
            {tapes.length === 0 ? "No tapes in your collection yet." : "No tapes match your search or filters."}
          </div>
          {tapes.length === 0 && (
            <button onClick={onAdd} style={{ background: "none", border: `1px solid ${T.border}`, color: T.accent, padding: "8px 16px", borderRadius: 6, cursor: "pointer", fontFamily: T.font, fontSize: 12 }}>Add your first tape</button>
          )}
        </div>
      ) : view === "grid" ? (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          {filtered.map((t) => (
            <div key={t.id} onClick={() => onSelect(t)} style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 10, padding: 12, cursor: "pointer", boxShadow: "0 1px 4px rgba(59,42,26,0.06)" }}>
              <div style={{ display: "flex", justifyContent: "center", marginBottom: 8 }}>
                {t.mainPhotoUrl ? <img src={t.mainPhotoUrl} alt={t.albumTitle} style={{ width: 60, height: 60, objectFit: "cover", borderRadius: 6 }} /> : <TapeIcon size={52} />}
              </div>
              <div style={{ fontSize: 12, fontWeight: "bold", color: T.text, textAlign: "center", marginBottom: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{t.albumTitle}</div>
              <div style={{ fontSize: 10, color: T.muted, textAlign: "center", marginBottom: 6 }}>{t.artist}</div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 6 }}>
                {t.condition ? <Badge condition={t.condition} /> : <span />}
                <span style={{ fontSize: 12, fontWeight: "bold", color: T.value }}>{fmt(t.estimatedValue)}</span>
              </div>
            </div>
          ))}
        </div>
      ) : (
        filtered.map((t) => (
          <div key={t.id} onClick={() => onSelect(t)} style={{ display: "flex", alignItems: "center", gap: 12, background: T.card, border: `1px solid ${T.border}`, borderRadius: 9, padding: "10px 12px", marginBottom: 8, cursor: "pointer" }}>
            {t.mainPhotoUrl ? <img src={t.mainPhotoUrl} alt={t.albumTitle} style={{ width: 40, height: 40, objectFit: "cover", borderRadius: 6 }} /> : <TapeIcon size={40} />}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: "bold", color: T.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{t.albumTitle}</div>
              <div style={{ fontSize: 11, color: T.muted }}>{t.artist}{t.releaseYear ? ` · ${t.releaseYear}` : ""}</div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 14, fontWeight: "bold", color: T.value }}>{fmt(t.estimatedValue)}</div>
              {t.condition && <Badge condition={t.condition} />}
            </div>
          </div>
        ))
      )}
      <div style={{ height: 70 }} />
      <button onClick={onAdd} style={{
        position: "fixed", bottom: 76, right: 20, width: 52, height: 52, borderRadius: 26,
        background: T.btnBg, border: "none", color: "#fff", fontSize: 24, cursor: "pointer",
        boxShadow: "0 4px 16px rgba(196,90,45,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 30
      }}>+</button>
    </div>
  );
}
